import axios from '@api';
import {
  transformClientCredits,
  transformClientCreditsforApi,
  transformClientSubscriptionData,
  transformUserCreditsData,
  transformUserCreditsForApi,
  transformUserSubscriptionData,
} from './utilities/helpers';

/**
 * Fetch client credit information for the current client.
 * @returns {Promise} Resolves to an object containing client credit values.
 */
export const readClientCredits = () => {
  return axios({
    method: 'get',
    transformResponse: [(data) => transformClientCredits(JSON.parse(data))],
    url: '/api/client/credits',
    withCredentials: true,
  });
};

/**
 * Fetch subscription information for the current client.
 * @returns {Promise} Resolves to an object containing subscription plan, renewal date, and usage.
 */
export const readClientSubscription = () => {
  return axios({
    method: 'get',
    transformResponse: [(data) => transformClientSubscriptionData(JSON.parse(data))],
    url: '/api/client/subscriptions',
    withCredentials: true,
  }).then((response) => response.data);
};

/**
 * Reads all users in a client including current user with survey permissions and returns credit details
 * @return {Promise}
 */
export const readClientUserCredits = () => {
  return axios({
    method: 'get',
    transformResponse: [(data) => transformUserCreditsData(JSON.parse(data))],
    url: '/api/client/users/credits',
    withCredentials: true,
  });
};

/**
 * Reads all users in a client including current user with survey permissions and returns subscription details
 * @param {Object} options
 * @param {number} options.page
 * @param {number} options.pageSize
 * @return {Promise}
 */
export const readClientUsersSubscription = ({ page, pageSize } = {}) => {
  return axios({
    method: 'get',
    params: {
      page,
      page_size: pageSize,
    },
    transformResponse: [(data) => transformUserSubscriptionData(JSON.parse(data))],
    url: '/api/subscription/users',
    withCredentials: true,
  }).then((response) => response.data);
};

/**
 * Updates credits for multiple users
 * @param {Array} data - Array of user credit data objects containing id and allocatedCredits
 * @returns {Promise<Object>} Promise that resolves to the transformed user credits data
 */
export const updateClientUserCredits = (data) => {
  return axios({
    data: {
      users: transformUserCreditsForApi(data),
    },
    method: 'post',
    transformResponse: [(data) => transformUserCreditsData(JSON.parse(data))],
    url: '/api/client/users/credits',
    withCredentials: true,
  });
};

/**
 * Updates credits for the current client
 * @param {Object} data - Object containing credit information (availableCredits, holdForFutureProjects, inFlightCredits)
 * @returns {Promise<Object>} Promise that resolves to the transformed client credits data
 */
export const updateClientCredits = (data) => {
  return axios({
    data: transformClientCreditsforApi(data),
    method: 'post',
    transformResponse: [(data) => transformClientCredits(JSON.parse(data))],
    url: '/api/client/credits',
    withCredentials: true,
  });
};
